import { Fragment, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as messagesApi from '../api/Messages';
import { IMailTemplateDetails } from '../util/DataModelInterface';
import { Spinner } from './Spinner';
import useToastHandler from '../hooks/useToastHandler';
import { ToastWrap } from './ToastWrap';

const MAIL_TEMPLATES = [
  'confirm',
  'reminder',
  'reminder2',
  'periodOpen'
]

export const MailTemplates = () => {
    const intl = useIntl();
    const { addToast } = useToastHandler();

    const [templateData, setTemplateData] = useState([] as IMailTemplateDetails[]);
    const [loading, setLoading] = useState(false);
    const [toastActive, setToastActive] = useState(false);
    const [toastHeader, setToastHeader] = useState("");
    const [toastText, setToastText] = useState("");
    const [toastType, setToastType] = useState("");
    const [open, setOpen] = useState({} as {[x: number]: boolean});
    const [changedDetails, setChangedDetails] = useState({
      confirm: false,
      reminder: false,
      reminder2: false,
      periodOpen: false
    } as {[x: string]: boolean});

    useEffect(() => {
      setLoading(true);
      refreshData()
      .finally(() => {
          setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshData = async (force: boolean = false) => {
      // [{"message_id": "confirm", ...}]
      await messagesApi.getMessages(force).then((data: any) => {
          if (data?.error) {
              addToast(data.error, 'messages', 'fetch');
          } else if (data) {
              // console.log("messages: ", data);
              setTemplateData(data);
          }
      });
  }

    const saveTemplate = async (message: string) => {

      setLoading(true);
      let temp = templateData.find((item) => item.message_id === message ) as IMailTemplateDetails;
      let resp = await messagesApi.modifyMessage(temp);
      if (resp?.error) {
        setToastHeader(intl.formatMessage({id: 'error.messages.save'}));
        setToastType('warning');
        setToastActive(true);
      } else {
        setSavedStates(message);
        await refreshData(true);
      }
      setLoading(false);
    }

    const toggleOneDetailSections = (index: number, e: any) => {
      if (open[index] !== e.target.open) {
          setOpen({...open, [index]: e.target.open});
      }
  }

  const update = (event: { currentTarget: any; }, message: string) => {

    const target = event.currentTarget;
    let newTemplateData = [...templateData] as any;
    let messageIndex = templateData.findIndex((item) => item.message_id === message );
    let text = target.value;
    newTemplateData[messageIndex][target.name] = text?.replaceAll('\n', '<br>\n');

    let className = target.name + '_class';
    newTemplateData[messageIndex][className] = 'fieldchanged';
    setTemplateData(newTemplateData);

    setChangedDetails({...changedDetails, [message]: true});
  }

  // Set states to saved
  const setSavedStates = (message: string) => {
    let newTemplateData = [...templateData] as any;
    let messageIndex = templateData.findIndex((item) => item.message_id === message );
    let messageItem = newTemplateData[messageIndex];
    Object.keys(messageItem).forEach((key) => {
      if (key.includes('_class') && messageItem[key] === 'fieldchanged') {
        messageItem[key] = 'fieldsaved';
      }
    });
    setTemplateData(newTemplateData);
    setToastHeader(intl.formatMessage({id: 'languages.message.saved'}));
    setToastText("");
    setToastType('success');
    setToastActive(true);

    setChangedDetails({...changedDetails, [message]: false});
  }

  const getTemplateItems = (message: string, tempIndex: number) => {
    let temp = templateData.find((item) => item.message_id === message );
    if (!temp){
      return null;
    }
    let title_fi = temp.title_fi ? temp.title_fi : "";
    let title_se = temp.title_se ? temp.title_se : "";
    let title_en = temp.title_en ? temp.title_en : "";
    let text_fi = temp.text_fi ? temp.text_fi.replaceAll('<br>\n', '\n') : "";
    let text_se = temp.text_se ? temp.text_se.replaceAll('<br>\n', '\n') : "";
    let text_en = temp.text_en ? temp.text_en.replaceAll('<br>\n', '\n') : "";
      return (
        <Fragment key={tempIndex}>
          <details open={open[tempIndex]} onToggle={(event) => toggleOneDetailSections(tempIndex, event)}>
            <summary className={ changedDetails[message] ? "changed" : "" }>
              <h2>
                <FormattedMessage id={"languages.templates." + message}/>
              </h2>
            </summary>
            <div className="details-content">

              <fieldset>
                <legend><FormattedMessage id="languages.finnish"/></legend>
                <div className="form-group">
                  <label htmlFor="title_fi" className="form-label"><FormattedMessage id="languages.message.title"/></label>
                  <input id="title_fi" type="text" name="title_fi"
                    autoComplete="off" onChange={(e) => update(e, message)}
                    className={"form-control " + temp.title_fi_class} value={title_fi}/>
                </div>
                <div className="form-group">
                  <label htmlFor="text_fi" className="form-label"><FormattedMessage id="languages.message.content"/></label>
                  <textarea id="text_fi" name="text_fi" rows={5} style={{whiteSpace: 'pre'}}
                    autoComplete="off" onChange={(e) => update(e, message)}
                    className={"form-control " + temp.text_fi_class} value={text_fi}></textarea>
                </div>
              </fieldset>

              <fieldset>
                <legend><FormattedMessage id="languages.swedish"/></legend>
                <div className="form-group">

                  <label htmlFor="title_se" className="form-label"><FormattedMessage id="languages.message.title"/></label>
                  <input id="title_se" type="text" name="title_se"
                    autoComplete="off" onChange={(e) => update(e, message)}
                    className={"form-control " + temp.title_se_class} value={title_se}/>
                </div>
                <div className="form-group">
                  <label htmlFor="text_se" className="form-label"><FormattedMessage id="languages.message.content"/></label>
                  <textarea id="text_se" name="text_se" rows={5}
                    autoComplete="off" onChange={(e) => update(e, message)}
                    className={"form-control " + temp.text_se_class} value={text_se}></textarea>
                </div>
              </fieldset>

              <fieldset>
                <legend><FormattedMessage id="languages.english"/></legend>
                <div className="form-group">
                  <label htmlFor="title_en" className="form-label"><FormattedMessage id="languages.message.title"/></label>
                  <input id="title_en" type="text" name="title_en"
                    autoComplete="off" onChange={(e) => update(e, message)}
                    className={"form-control " + temp.title_en_class} value={title_en}/>
                </div>
                <div className="form-group">
                  <label htmlFor="text_en" className="form-label"><FormattedMessage id="languages.message.content"/></label>
                  <textarea id="text_en" name="text_en" rows={5} style={{whiteSpace: 'pre'}}
                    autoComplete="off" onChange={(e) => update(e, message)}
                    className={"form-control " + temp.text_en_class} value={text_en}></textarea>
                </div>
              </fieldset>

            </div>

            <div className="form-buttons">
              <button type="button" className="btn btn-primary btn-save" disabled={!changedDetails[message]}
                onClick={(e) => saveTemplate(message)}>
                <i className="fa fa-save me-2" aria-hidden="true"></i>
                <FormattedMessage id="button.save"/>
              </button>
            </div>

          </details>
        </Fragment>
      )
  }

    return (
      <div id="tab-tabpane-templates" role="tabpanel" aria-labelledby="tab-tab-templates">
        <Spinner active={loading} />
        <ToastWrap active={toastActive} heading={toastHeader} text={toastText} type={toastType} setActive={(active: boolean) => setToastActive(active)} />
        <div className="alert alert-info"><i className="fa fa-info-circle mr-2"></i>
          <div>
            <p><FormattedMessage id="languages.templates.usage"/></p>
            <p>
              <FormattedMessage id="languages.templates.period"/> {'{PERIOD}'}<br></br>
              <FormattedMessage id="languages.templates.company"/> {'{COMP}'}<br></br>
              <FormattedMessage id="languages.templates.deadline"/> {'{DEADLINE}'}
            </p>
          </div>
        </div>
        <div id="messagesList">

        {MAIL_TEMPLATES.map((message, index) =>
          getTemplateItems(message, index))}

        </div>
      </div>
    );
};